import React from "react"

import Commandbar from "~/components/planning/Commandbar"
import { Tooltip } from "~/components/reusable/Tooltip"
import { formatNumber } from "~/utils/formatting"

import { type ColResolver, evaluateFormula, PREC, precedenceOf } from "./ast"
import { flipSign, type Formula, type Role, ROLE_COLORS } from "./types"

type FormulaBarProps = {
  /** Formula AST of the inspected column. */
  formula: Formula
  /** The inspected row, as a plain record. */
  row: Record<string, unknown>
  /** Display labels for each `col` referenced by the formula. */
  columnLabels: Record<string, string>
  /** Scalar values for `param` nodes. */
  params: Record<string, number>
  /** Display labels for `param` nodes. */
  paramLabels: Record<string, string>
  /**
   * Optional contextual note rendered after the result, separated by a divider.
   * Use to explain post-formula adjustments (e.g. "Adjusted to 144 to meet MOQ").
   */
  note?: string
  cellValue?: number
  /**
   * Optional override for resolving `col(...)` operand values. Defaults to
   * reading `row[colId]`. DataTableV2 builds a resolver that consults each
   * column's `context.relationalFormulaValue` for valueGetter-based columns.
   */
  resolveCol?: ColResolver
  onClear: () => void
}

/**
 * Walks the AST of the inspected column's formula, substitutes values from the
 * inspected row, and renders the resulting expression with per-operand color
 * coding driven by each node's sign in the tree.
 */
export function FormulaBar({
  formula,
  row,
  columnLabels,
  params,
  paramLabels,
  note,
  cellValue,
  resolveCol,
  onClear,
}: FormulaBarProps) {
  const rawResult = evaluateFormula(formula, row, params, resolveCol)
  // Assume the cell value is absolute truth, if it differs from formula result, add formatting so we 'round' to the cell value
  const showRoundedChip =
    cellValue !== undefined && Number.isFinite(cellValue) && cellValue !== rawResult
  return (
    // `[&_.fixed]:!bottom-[50px]`: push the floating bar up off the bottom.
    // `count={1}` is required (Commandbar bails when count===0) but we hide
    // the badge + label region via `hideHeader` so the bar starts at the formula.
    <div data-formula-bar className="[&_.fixed]:!bottom-[50px]">
      <Commandbar
        count={1}
        hideHeader
        onClear={onClear}
        ariaLabel="Formula breakdown"
        clearButtonAriaLabel="Clear formula breakdown"
        className="overflow-hidden bg-base pl-3 pr-2 min-h-11 paragraph-base gap-3"
      >
        <FormulaNode
          node={formula}
          row={row}
          columnLabels={columnLabels}
          params={params}
          paramLabels={paramLabels}
          sign={1}
          resolveCol={resolveCol}
        />
        <Op symbol="=" />
        <Chip value={rawResult} role="result" />
        {showRoundedChip && (
          <>
            <Op symbol="≈" />
            <Chip value={cellValue as number} role="result" />
          </>
        )}
        {note && (
          // -my-3 + self-stretch lets this region eat the bar's vertical
          // padding so the bg-subtle label and the flanking dividers extend
          // from the top edge of the bar to the bottom.
          <span className="-my-3 flex items-stretch self-stretch">
            <span className="w-px bg-fg-muted/30" aria-hidden />
            <span className="flex items-center whitespace-pre-line bg-subtle px-4 py-2 text-fg-default">
              {note}
            </span>
            <span className="w-px bg-fg-muted/30" aria-hidden />
          </span>
        )}
      </Commandbar>
    </div>
  )
}

function FormulaNode({
  node,
  row,
  columnLabels,
  params,
  paramLabels,
  sign,
  forcedRole,
  parentPrecedence = 0,
  resolveCol,
}: {
  node: Formula
  row: Record<string, unknown>
  columnLabels: Record<string, string>
  params: Record<string, number>
  paramLabels: Record<string, string>
  sign: 1 | -1
  forcedRole?: Role
  /** Required precedence for this node to render without parens. */
  parentPrecedence?: number
  resolveCol?: ColResolver
}): React.ReactNode {
  const leafRole: Role = forcedRole ?? (sign === 1 ? "addend" : "detractor")
  const content = (() => {
    switch (node.kind) {
      case "col": {
        const value = resolveCol ? resolveCol(node.id) : Number(row[node.id] ?? 0)
        return <Chip value={value} role={leafRole} label={columnLabels[node.id]} />
      }
      case "const":
        return <Chip value={node.value} role={leafRole} />
      case "param":
        return <Chip value={params[node.id] ?? 0} role={leafRole} label={paramLabels[node.id]} />
      case "sum":
        return (
          <>
            {node.terms.map((t, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <Op symbol="+" />}
                <FormulaNode
                  node={t}
                  row={row}
                  columnLabels={columnLabels}
                  params={params}
                  paramLabels={paramLabels}
                  sign={sign}
                  forcedRole={forcedRole}
                  parentPrecedence={PREC.additive}
                  resolveCol={resolveCol}
                />
              </span>
            ))}
          </>
        )
      case "diff":
        return (
          <>
            <FormulaNode
              node={node.from}
              row={row}
              columnLabels={columnLabels}
              params={params}
              paramLabels={paramLabels}
              sign={sign}
              forcedRole={forcedRole}
              parentPrecedence={PREC.additive}
              resolveCol={resolveCol}
            />
            {node.minus.map((m, i) => (
              <span key={i} className="flex items-center gap-2">
                <Op symbol="-" />
                <FormulaNode
                  node={m}
                  row={row}
                  columnLabels={columnLabels}
                  params={params}
                  paramLabels={paramLabels}
                  sign={flipSign(sign)}
                  forcedRole={forcedRole}
                  // Right side of subtraction needs parens for sum/diff
                  // children — `a - (b + c) ≠ a - b + c`.
                  parentPrecedence={PREC.multiplicative}
                  resolveCol={resolveCol}
                />
              </span>
            ))}
          </>
        )
      case "ratio":
        return (
          <span className="flex items-center gap-2">
            <FormulaNode
              node={node.num}
              row={row}
              columnLabels={columnLabels}
              params={params}
              paramLabels={paramLabels}
              sign={sign}
              forcedRole={forcedRole}
              parentPrecedence={PREC.multiplicative}
              resolveCol={resolveCol}
            />
            <Op symbol="÷" />
            <FormulaNode
              node={node.den}
              row={row}
              columnLabels={columnLabels}
              params={params}
              paramLabels={paramLabels}
              sign={sign}
              forcedRole="divisor"
              parentPrecedence={PREC.multiplicative}
              resolveCol={resolveCol}
            />
          </span>
        )
      case "product":
        return (
          <>
            {node.factors.map((f, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <Op symbol="·" />}
                <FormulaNode
                  node={f}
                  row={row}
                  columnLabels={columnLabels}
                  params={params}
                  paramLabels={paramLabels}
                  sign={sign}
                  forcedRole={forcedRole}
                  parentPrecedence={PREC.multiplicative}
                  resolveCol={resolveCol}
                />
              </span>
            ))}
          </>
        )
    }
  })()

  if (precedenceOf(node) >= parentPrecedence) return content
  return (
    <span className="flex items-center gap-2">
      <Op symbol="(" />
      {content}
      <Op symbol=")" />
    </span>
  )
}

function Op({ symbol }: { symbol: string }) {
  return (
    <span className="inline-flex h-6 items-center justify-center text-fg-muted leading-none label-sm">
      {symbol}
    </span>
  )
}

function Chip({ value, role, label }: { value: number; role: Role; label?: string }) {
  const colors = ROLE_COLORS[role]
  const formatted = Number.isInteger(value) ? formatNumber(value) : formatNumber(value, 2)
  const chip = (
    <span
      className="inline-flex h-6 items-center justify-center rounded px-1.5 font-mono label-sm leading-none"
      style={{ backgroundColor: colors.bg, color: colors.fg }}
    >
      {formatted}
    </span>
  )
  if (!label) return chip
  return <Tooltip title={label}>{chip}</Tooltip>
}
